<template>    
    <form-modal
        title="Create Document Template"
        @submit="createDocumentTemplate"
        :loading="loading"
        v-model="isShowCreateEditor">
        <fieldset>
            <div class="mb-3 row">
                <label
                    for="documentTemplateName"
                    class="col-sm-3 col-form-label">
                    Name:</label
                >
                <div class="col-sm-9">
                    <b-input
                        id="documentTemplateName"
                        name="documentTemplateName"
                        type="text"
                        v-model="documentTemplate.name"
                        :error="errors.name" />
                </div>
            </div>

            <div class="mb-3 row">
                <label
                    for="documentTemplateDescription"
                    class="col-sm-3 col-form-label"
                    >Description:</label
                >
                <div class="col-sm-9">
                    <b-textarea
                        class="form-control"
                        name="documentTemplateDescription"
                        v-model="documentTemplate.description"
                        :error="errors.description" />
                </div>
            </div>
            <div class="mb-3 row">
                <label
                    for="documentTemplateFile"
                    class="col-sm-3 col-form-label"
                    >File:</label
                >
                <div class="col-sm-9">
                    <b-file-input
                        v-model="documentTemplate.file"
                        name="documentTemplateFile"
                        :error="errors.file"></b-file-input>
                </div>
            </div>
        </fieldset>
    </form-modal>

    <form-modal
        title="Replace Document"
        :loading="loading"
        @submit="replaceDocumentTemplate"
        v-model="isShowReplaceDocument">
        <fieldset>
            <div class="mb-3 row">
                <label
                    for="replaceDocumentTemplateFile"
                    class="col-sm-3 col-form-label">
                    File:
                </label>
                <div class="col-sm-9">
                    <b-file-input
                        v-model="replaceDocument.file"
                        name="replaceDocumentTemplateFile"
                        :error="errors.file"></b-file-input>
                </div>
            </div>
        </fieldset>
    </form-modal>
    <system-alerts></system-alerts>
    <div class="document-templates-grid">
        <b-grid
            caption="List of document templates"
            :tableOptions="auditDocumentTemplateStore.tableOptions"
            :filterableFields="auditDocumentTemplateStore.filterableFields"
            :filterOptions="auditDocumentTemplateStore.filterOptions"
            @optionChange="getData"
            :items="auditDocumentTemplateStore.items"
            :columns="columns"
            :loading="auditDocumentTemplateStore.loading">
            <template #beforeFilter>
                <button
                    @click.prevent="showCreateEditor"
                    v-if="canAdd"
                    class="btn btn-primary">
                    Create Document Template
                </button>
            </template>
            <template #cell.actions="{item}">
                <template v-if="item.fileId">
                    <a
                        class="btn btn-sm btn-secondary me-2"
                        :href="`api/v1/audit/file/${item.fileId}`"
                        :aria-label="`View document ${item.name}`"
                        >View Document</a
                    >
                </template>

                <button
                    @click.prevent="showReplaceDocument(item.id)"
                    :aria-label="`Edit ${item.name}`"
                    type="button"
                    v-if="canDelete"
                    class="btn btn-sm btn-primary me-2">
                    Edit
                </button>

                <button
                    @click.prevent="deleteDocument(item.id)"
                    :aria-label="`Delete ${item.name}`"
                    type="button"
                    v-if="canDelete"
                    class="btn btn-sm btn-danger">
                    Delete
                </button>
                <span v-else></span>
            </template>
        </b-grid>
    </div>
    <confirm-modal></confirm-modal>
</template>
<script lang="ts" setup>
import {ref, reactive, computed} from "vue";
import BGrid from "./BGrid/BGrid.vue";
import type {BTableColumn} from "./BTable/BTable.types";
import {FormModal, SystemAlerts, ConfirmModal} from "./Misc";
import type {DocumentTemplate, DocumentTemplateUpdateRequest} from "../audit.types";
import {useFormValidation} from "../validation";
import {BInput, BTextarea, BFileInput} from "./Fields";
import {useAuditDocumentTemplateStore} from "@/stores/auditDocumentTemplateStore";
import {object, string, date, mixed} from "yup";
import {storeToRefs} from "pinia";
import {useConfirmDialog} from "@/composable/useConfirmDialog";
const {confirm} = useConfirmDialog();

const props = defineProps<{
    title: String;
    runAudits: boolean;
    createAudits: boolean;
}>();

const documentTemplate = ref<DocumentTemplate>({} as DocumentTemplate);
const isShowCreateEditor = ref(false);
const auditDocumentTemplateStore = useAuditDocumentTemplateStore();

const {loading} = storeToRefs(auditDocumentTemplateStore);

const schema = object<DocumentTemplate>({
    name: string().required().max(50).label("Name"),
    description: string().nullable().max(500).label("Description"),
    file: mixed().required().label("File"),
});

const columns = reactive<BTableColumn[]>([
    {
        key: "name",
        text: "Name",
        sortable: true,
        columnClass: ["align-top"],
        value: (item) => item["name"],
    },
    {
        key: "description",
        text: "Description",
        sortable: true,
        columnClass: ["align-top"],
        value: (item) => item["description"],
    },
    {
        key: "uploadedBy",
        text: "Uploaded By",
        sortable: true,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.uploadedBy,
    },
    {
        key: "uploadDate",
        text: "Upload Date",
        sortable: true,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.uploadDate,
    },
    {
        key: "actions",
        text: "Actions",
        sortable: false,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap text-end"],
        value: (item) => item["link"],
    },
]);

const showCreateEditor = () => {
    resetValidation();
    documentTemplate.value = {} as DocumentTemplate;
    isShowCreateEditor.value = true;
};

const getData = async () => {
    await auditDocumentTemplateStore.list();
};

const canAdd = computed(() => props.createAudits);

const canDelete = (): boolean => props.runAudits;

const isShowDeleteDialog = ref(false);

const hideDeleteConfirmEditor = async function () {
    isShowDeleteDialog.value = false;
};

const deleteDocument = async (id: number) => {
    const shouldProceed = await confirm("Delete Confirmation", "Are You Sure?");
    if (shouldProceed) {
        await auditDocumentTemplateStore.deleteItem(id);
        hideDeleteConfirmEditor();
        getData();
    }
};

const isShowReplaceDocument = ref(false);

const replaceDocumentSchema = object<DocumentTemplate>({
    file: mixed().required().label("File"),
});
const replaceDocument = ref<DocumentTemplateUpdateRequest>({} as DocumentTemplateUpdateRequest);

const showReplaceDocument = async (id: number) => {
    resetValidation();
    replaceDocument.value = {} as DocumentTemplateUpdateRequest;
    isShowReplaceDocument.value = true;
    replaceDocument.value.id = id;
};

const replaceDocumentTemplate = async () => {
    if (await handleSubmit(replaceDocumentSchema, replaceDocument.value)) {
        await auditDocumentTemplateStore.replace(replaceDocument.value);
        await getData();
        isShowReplaceDocument.value = false;
    }
};

const {handleSubmit, errors, resetValidation} = useFormValidation();

const createDocumentTemplate = async () => {
    if (await handleSubmit(schema, documentTemplate.value)) {
        await auditDocumentTemplateStore.create(documentTemplate.value);
        await getData();
        isShowCreateEditor.value = false;
        documentTemplate.value = {} as DocumentTemplate;
    }
};
await getData();
</script>
